import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import { TILE_SIZE } from "@cold-case/shared/constants";
import Phaser from "phaser";

import type { CoherenceJournal } from "../lib/dialog-parser.js";
import { resolveBuildingInvestigation } from "../lib/building-investigation.js";
import {
  BUILDING_FOOTPRINT_ALPHA_TOLERANCE,
  buildingAnchorFromSprite,
  buildingMaskWorldBounds,
  worldPointHitsBuildingOpaqueMask,
} from "./building-art-footprint.js";
import {
  entryArrowDisplayHitHalfExtents,
  entryArrowInteractMaxDistPx,
  entryArrowWorldPoint,
} from "./building-entry-arrow-placement.js";

export {
  ENTRY_ARROW_TEX_H,
  ENTRY_ARROW_TEX_W,
  entryArrowDisplayHitHalfExtents,
  entryArrowInteractMaxDistPx,
} from "./building-entry-arrow-placement.js";
import { ensureTransitChevronTexture } from "./transit-chevron.js";
import type { MapGrid } from "./map-grid.js";

export {
  ENTRY_ARROW_ROTATION_UP,
  entryArrowRotationToward,
  entryArrowStandWorldPoint,
  entryArrowWorldPoint,
} from "./building-entry-arrow-placement.js";
export type { EntryArrowPlacement } from "./building-entry-arrow-placement.js";

const ENTRY_ARROW_KEY = "building_entry_chevron";
const GREEN_PALETTE = { fill: 0x22c55e, stroke: 0x86efac };
const RED_PALETTE = { fill: 0xb91c1c, stroke: 0xfca5a5 };

export type BuildingEntryArrowHost = {
  scene: Phaser.Scene;
  mapGrid: MapGrid;
  buildingSprites: Map<string, Phaser.GameObjects.Sprite>;
  getManifest: () => CaseManifestV1 | null;
  getJournal: () => CoherenceJournal;
  investigationOpts: () => { userId?: string; playerCount?: number; manifest?: CaseManifestV1 };
  interactionBlocked: () => boolean;
  onGreenTap: (buildingId: string) => void;
  onRedTap: (buildingId: string) => void;
};

export type BuildingEntryArrowState = {
  spr: Phaser.GameObjects.Image;
  buildingId: string;
  permitted: boolean;
};

function ensureEntryTextures(scene: Phaser.Scene): void {
  ensureTransitChevronTexture(scene, `${ENTRY_ARROW_KEY}_green`, GREEN_PALETTE);
  ensureTransitChevronTexture(scene, `${ENTRY_ARROW_KEY}_red`, RED_PALETTE);
}

function setEntryArrowInteractive(spr: Phaser.GameObjects.Image): void {
  spr.setInteractive({
    pixelPerfect: true,
    alphaTolerance: 1,
    cursor: "pointer",
  });
  if (!spr.input) {
    const { halfW, halfH } = entryArrowDisplayHitHalfExtents(
      spr.displayWidth,
      spr.displayHeight
    );
    spr.setInteractive(
      new Phaser.Geom.Rectangle(-halfW, -halfH, halfW * 2, halfH * 2),
      Phaser.Geom.Rectangle.Contains
    );
    spr.input!.cursor = "pointer";
  }
}

export function syncBuildingEntryArrows(
  host: BuildingEntryArrowHost,
  arrows: Map<string, BuildingEntryArrowState>
): void {
  ensureEntryTextures(host.scene);
  const manifest = host.getManifest();
  if (!manifest) return;
  const journal = host.getJournal();
  const invBase = host.investigationOpts();
  const seen = new Set<string>();
  const tileUsers = new Map<string, number>();

  const buildings: { bid: string; spr: Phaser.GameObjects.Sprite }[] = [];
  for (const [bid, bldSpr] of host.buildingSprites) {
    if (typeof bid !== "string" || !bid.trim()) continue;
    if (bldSpr.getData("decor") === true) continue;
    buildings.push({ bid, spr: bldSpr });
  }
  buildings.sort((a, b) => {
    const ax = (a.spr.getData("foot_left") as number) + (a.spr.getData("foot_span") as number);
    const bx = (b.spr.getData("foot_left") as number) + (b.spr.getData("foot_span") as number);
    return ax - bx || a.bid.localeCompare(b.bid);
  });

  for (const { bid, spr: bldSpr } of buildings) {
    if (typeof bid !== "string" || !bid.trim()) continue;
    seen.add(bid);
    const footLeft = bldSpr.getData("foot_left") as number;
    const footTop = bldSpr.getData("foot_top") as number;
    const footSpan = bldSpr.getData("foot_span") as number;
    const spineWx = bldSpr.getData("spine_wx") as number;
    const spineWy = bldSpr.getData("spine_wy") as number;

    const anchor = buildingAnchorFromSprite(bldSpr);
    const maskBounds = buildingMaskWorldBounds(
      bid,
      anchor.anchorWorldX,
      anchor.anchorWorldY,
      bldSpr.displayWidth,
      bldSpr.displayHeight
    );
    const facadeBottomY =
      maskBounds?.bottom ??
      bldSpr.y + bldSpr.displayHeight * (1 - bldSpr.originY);
    const facadeCenterX =
      maskBounds != null ? (maskBounds.left + maskBounds.right) / 2 : anchor.anchorWorldX;
    const placement = entryArrowWorldPoint(
      footLeft,
      footTop,
      footSpan,
      spineWx,
      spineWy,
      host.mapGrid,
      tileUsers.size,
      {
        anchorWorldX: anchor.anchorWorldX,
        anchorWorldY: anchor.anchorWorldY,
        facadeCenterX,
        facadeBottomY,
        hitsOpaque: (wx, wy) =>
          worldPointHitsBuildingOpaqueMask({
            assetId: bid,
            anchorWorldX: anchor.anchorWorldX,
            anchorWorldY: anchor.anchorWorldY,
            displayWidth: bldSpr.displayWidth,
            displayHeight: bldSpr.displayHeight,
            wx,
            wy,
            alphaThreshold: BUILDING_FOOTPRINT_ALPHA_TOLERANCE,
            footprintLot: {
              left: footLeft,
              top: footTop,
              span: footSpan,
            },
          }),
      }
    );
    const tileKey = `${Math.floor(placement.wx / TILE_SIZE)},${Math.floor(placement.wy / TILE_SIZE)}`;
    tileUsers.set(tileKey, (tileUsers.get(tileKey) ?? 0) + 1);

    const b = manifest.buildings.find((x) => x.building_id === bid) ?? null;
    const res = resolveBuildingInvestigation(b, journal, { ...invBase, manifest });
    const permitted = res.permitted;
    const texKey = permitted ? `${ENTRY_ARROW_KEY}_green` : `${ENTRY_ARROW_KEY}_red`;

    let state = arrows.get(bid);
    if (!state) {
      const spr = host.scene.add.image(placement.wx, placement.wy, texKey);
      spr.setRotation(placement.rotation);
      spr.setDepth(4.5);
      spr.setAlpha(0.95);
      setEntryArrowInteractive(spr);
      spr.on("pointerdown", (
        _p: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
        if (host.interactionBlocked()) return;
        const cur = arrows.get(bid);
        if (!cur) return;
        if (cur.permitted) host.onGreenTap(bid);
        else host.onRedTap(bid);
      });
      host.scene.tweens.add({
        targets: spr,
        alpha: { from: 0.55, to: 1 },
        duration: 650,
        yoyo: true,
        repeat: -1,
      });
      state = { spr, buildingId: bid, permitted };
      arrows.set(bid, state);
    }

    state.spr.setPosition(placement.wx, placement.wy);
    state.spr.setRotation(placement.rotation);
    state.spr.setTexture(texKey);
    state.permitted = permitted;
  }

  for (const [bid, state] of arrows) {
    if (!seen.has(bid)) {
      state.spr.destroy();
      arrows.delete(bid);
    }
  }
}

export function destroyBuildingEntryArrows(arrows: Map<string, BuildingEntryArrowState>): void {
  for (const state of arrows.values()) {
    state.spr.destroy();
  }
  arrows.clear();
}

/** Nearest entry arrow within interact range (for E-key). */
export function findBuildingEntryArrowAt(
  arrows: Map<string, BuildingEntryArrowState>,
  wx: number,
  wy: number,
  maxDistPx = entryArrowInteractMaxDistPx()
): string | null {
  let best: string | null = null;
  let bestD = maxDistPx + 1;
  for (const [bid, state] of arrows) {
    const d = Math.hypot(state.spr.x - wx, state.spr.y - wy);
    if (d <= maxDistPx && d < bestD) {
      bestD = d;
      best = bid;
    }
  }
  return best;
}
